/**
 * /strategy — Strategy documents (current + history).
 *
 * Layout:
 *   - Header with status filter
 *   - Currently active strategy rendered as Markdown
 *   - List of past/proposed/archived strategies below
 *
 * Founder/admin sees "Propose strategy" → opens a slide-over form. The
 * upstream gates approval via Temporal FounderApprovalGate.
 */
import type { Strategy, StrategyProposal } from '@chaos/types/constitution';
import { strategyProposalSchema } from '@chaos/types/constitution';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Compass, Loader2, Plus, X } from 'lucide-react';
import * as React from 'react';
import { Controller, useForm } from 'react-hook-form';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { MarkdownEditor } from '../../../components/editor/MarkdownEditor';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { useToaster } from '../../../components/ui/toast';
import { constitutionApi, constitutionKeys } from '../../../lib/api/constitution';
import { cn } from '../../../lib/utils';
import { useAuthStore } from '../../../stores/auth-store';

const STATUS_FILTERS = ['all', 'active', 'approved', 'draft', 'archived', 'retired'] as const;

function StrategyRoute() {
  const workspaces = useAuthStore((s) => s.workspaces);
  const activeWorkspaceId = useAuthStore((s) => s.activeWorkspaceId);
  const role =
    workspaces.find((w) => w.workspace.id === activeWorkspaceId)?.membership.role ?? null;
  const canEdit = role === 'founder' || role === 'admin';

  const [statusFilter, setStatusFilter] = React.useState<(typeof STATUS_FILTERS)[number]>('all');
  const [composing, setComposing] = React.useState(false);

  const strategiesQ = useQuery({
    queryKey: constitutionKeys.strategies(
      statusFilter === 'all' ? {} : { status: statusFilter as Exclude<typeof statusFilter, 'all'> },
    ),
    queryFn: () =>
      constitutionApi.listStrategies(
        statusFilter === 'all'
          ? {}
          : { status: statusFilter as Exclude<typeof statusFilter, 'all'> },
      ),
    staleTime: 30_000,
  });
  const strategies = strategiesQ.data?.strategies ?? [];
  const active = strategies.find((s) => s.status === 'active') ?? null;
  const rest = strategies.filter((s) => s.id !== active?.id);

  return (
    <section className="mx-auto flex w-full max-w-5xl flex-col gap-6 p-6">
      <header className="flex flex-wrap items-baseline gap-3">
        <Compass className="h-6 w-6 text-foreground" aria-hidden />
        <div className="flex flex-col">
          <h1 className="text-[18px] font-semibold tracking-tight text-foreground">Strategy</h1>
          <p className="text-[12px] text-muted-foreground">
            Quarterly direction — approved strategies inform OKRs and goal trees.
          </p>
        </div>
        <FilterChips value={statusFilter} onChange={setStatusFilter} />
        {canEdit ? (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setComposing((c) => !c)}
            className="ml-auto h-7"
          >
            {composing ? (
              <>
                <X className="mr-1.5 h-3.5 w-3.5" aria-hidden /> Cancel
              </>
            ) : (
              <>
                <Plus className="mr-1.5 h-3.5 w-3.5" aria-hidden /> Propose strategy
              </>
            )}
          </Button>
        ) : null}
      </header>

      {composing && canEdit ? <StrategyComposer onDone={() => setComposing(false)} /> : null}

      {active ? (
        <article className="rounded border border-foreground/10 bg-background p-5">
          <header className="mb-3 flex items-baseline gap-2">
            <StatusChip status="active" />
            <h2 className="text-[15px] font-semibold text-foreground">{active.title}</h2>
            <span className="ml-2 font-mono text-[11px] text-muted-foreground">
              {active.period}
            </span>
          </header>
          <div className="prose prose-sm max-w-none text-[13px] leading-relaxed dark:prose-invert">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{active.bodyMd}</ReactMarkdown>
          </div>
        </article>
      ) : null}

      <div className="rounded border border-border bg-background">
        <header className="border-b border-border bg-muted/30 px-3 py-1.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
          History
        </header>
        <ul className="divide-y divide-border">
          {strategiesQ.isLoading ? (
            <li className="px-3 py-6 text-center text-[12px] text-muted-foreground">
              Loading strategies…
            </li>
          ) : null}
          {!strategiesQ.isLoading && rest.length === 0 ? (
            <li className="px-3 py-6 text-center text-[12px] text-muted-foreground">
              No matching strategies.
            </li>
          ) : null}
          {rest.map((s) => (
            <StrategyRow key={s.id} strategy={s} />
          ))}
        </ul>
      </div>
    </section>
  );
}

function StrategyRow({ strategy }: { strategy: Strategy }) {
  return (
    <li className="flex items-baseline gap-2 px-3 py-2">
      <StatusChip status={strategy.status} />
      <span className="text-[13px] text-foreground">{strategy.title}</span>
      <span className="font-mono text-[11px] text-muted-foreground">{strategy.period}</span>
      <span className="ml-auto font-mono text-[10px] tabular-nums text-muted-foreground/70">
        {new Date(strategy.createdAt).toISOString().slice(0, 10)}
      </span>
    </li>
  );
}

function StatusChip({ status }: { status: Strategy['status'] }) {
  const labels: Record<Strategy['status'], string> = {
    draft: 'Draft',
    approved: 'Approved',
    active: 'Active',
    archived: 'Archived',
    retired: 'Retired',
  };
  const cls: Record<Strategy['status'], string> = {
    draft: 'border-warning/40 bg-warning/10 text-warning-foreground',
    approved: 'border-success/40 bg-success/10 text-success-foreground',
    active: 'border-success/60 bg-success/15 text-success-foreground',
    archived: 'border-border bg-muted text-muted-foreground',
    retired: 'border-border bg-muted text-muted-foreground',
  };
  return (
    <span
      className={cn(
        'rounded border px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wider',
        cls[status],
      )}
    >
      {labels[status]}
    </span>
  );
}

function FilterChips({
  value,
  onChange,
}: {
  value: (typeof STATUS_FILTERS)[number];
  onChange: (v: (typeof STATUS_FILTERS)[number]) => void;
}) {
  return (
    <div className="ml-3 flex items-center gap-0.5 rounded border border-border bg-background p-0.5 text-[11px]">
      {STATUS_FILTERS.map((opt) => (
        <button
          key={opt}
          type="button"
          aria-pressed={value === opt}
          onClick={() => onChange(opt)}
          className={cn(
            'rounded px-2 py-0.5 capitalize transition-colors',
            value === opt
              ? 'bg-foreground/10 text-foreground'
              : 'text-muted-foreground hover:bg-foreground/5 hover:text-foreground',
          )}
        >
          {opt}
        </button>
      ))}
    </div>
  );
}

function StrategyComposer({ onDone }: { onDone: () => void }) {
  const qc = useQueryClient();
  const { toast } = useToaster();
  const form = useForm<StrategyProposal>({
    resolver: zodResolver(strategyProposalSchema),
    defaultValues: {
      period: '',
      title: '',
      bodyMd: '',
      scope: 'global',
      metadata: {},
    },
  });

  const submit = useMutation({
    mutationFn: (input: StrategyProposal) => constitutionApi.proposeStrategy(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: constitutionKeys.all });
      toast({ title: 'Strategy proposal submitted' });
      onDone();
    },
    onError: (err: Error) => {
      toast({ title: 'Submit failed', description: err.message, variant: 'destructive' });
    },
  });
  const onSubmit = form.handleSubmit((values) => {
    submit.mutate(values);
  });

  return (
    <form
      onSubmit={onSubmit}
      className="flex flex-col gap-3 rounded border border-border bg-muted/20 p-4"
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div className="flex flex-col gap-1">
          <label htmlFor="strategy-period" className="text-[12px] font-medium text-foreground">
            Period
          </label>
          <Input
            id="strategy-period"
            placeholder="Q3 2026"
            {...form.register('period')}
            disabled={submit.isPending}
          />
        </div>
        <div className="flex flex-col gap-1 sm:col-span-2">
          <label htmlFor="strategy-title" className="text-[12px] font-medium text-foreground">
            Title
          </label>
          <Input
            id="strategy-title"
            placeholder="Solo founder GTM"
            {...form.register('title')}
            disabled={submit.isPending}
          />
        </div>
      </div>
      <div className="flex flex-col gap-1">
        <label htmlFor="strategy-body" className="text-[12px] font-medium text-foreground">
          Body (Markdown)
        </label>
        <Controller
          control={form.control}
          name="bodyMd"
          render={({ field }) => (
            <MarkdownEditor
              id="strategy-body"
              value={field.value ?? ''}
              onChange={field.onChange}
              placeholder="# Q3 2026\n## Bets\n- ...\n## Risks\n- ..."
              rows={14}
            />
          )}
        />
      </div>
      <div className="flex items-center gap-2">
        <Button type="submit" disabled={submit.isPending}>
          {submit.isPending ? (
            <>
              <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" aria-hidden />
              Submitting…
            </>
          ) : (
            'Submit proposal'
          )}
        </Button>
        <Button type="button" variant="ghost" onClick={onDone} disabled={submit.isPending}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

export const Route = createFileRoute('/_authenticated/strategy/')({
  component: StrategyRoute,
});
